import React, { useRef, useState, useCallback, useEffect } from 'react';
import TreeNode from './TreeNode';
import TreeEdge from './TreeEdge';
import { useFamilyTreeLayout } from './useFamilyTreeLayout';

const SVG_W = 960;

const FamilyTreeCanvas = ({ nodes, links, selectedNode, onSelectNode, nodeVerifStatus }) => {
  const svgRef       = useRef(null);
  const wrapRef      = useRef(null);
  const [pan,  setPan]  = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const dragging  = useRef(false);
  const lastPos   = useRef({ x: 0, y: 0 });
  const didDrag   = useRef(false);

  const positions = useFamilyTreeLayout(nodes, SVG_W);
  const maxY      = Object.values(positions).reduce((m, p) => Math.max(m, p.y), 0);
  const svgH      = maxY + 140;

  const connectedIds = selectedNode
    ? new Set(
        links
          .filter(l => l.source === selectedNode.id || l.target === selectedNode.id)
          .flatMap(l => [l.source, l.target])
      )
    : null;

  /* ── Pan ─────────────────────────────────────────────────────────────── */
  const onMouseDown = useCallback((e) => {
    dragging.current = true;
    didDrag.current  = false;
    lastPos.current  = { x: e.clientX, y: e.clientY };
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) didDrag.current = true;
    setPan(p => ({ x: p.x + dx, y: p.y + dy }));
    lastPos.current = { x: e.clientX, y: e.clientY };
  }, []);

  const onMouseUp = useCallback(() => { dragging.current = false; }, []);

  /* ── Zoom ────────────────────────────────────────────────────────────── */
  const onWheel = useCallback((e) => {
    e.preventDefault();
    setZoom(z => Math.min(2, Math.max(0.4, z - e.deltaY * 0.001)));
  }, []);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  /* ── Convert SVG-space node coords → container-relative px ──────────── */
  const getAnchorPos = useCallback((nodeId) => {
    const pos      = positions[nodeId];
    const svgEl    = svgRef.current;
    const wrapEl   = wrapRef.current;
    if (!pos || !svgEl || !wrapEl) return null;

    const svgRect  = svgEl.getBoundingClientRect();
    const wrapRect = wrapEl.getBoundingClientRect();

    const scaleX = svgRect.width  / SVG_W;
    const scaleY = svgRect.height / svgH;

    const worldX = pos.x * zoom + pan.x;
    const worldY = pos.y * zoom + pan.y;

    const renderedX = worldX * scaleX + (svgRect.left - wrapRect.left);
    const renderedY = worldY * scaleY + (svgRect.top  - wrapRect.top);

    return {
      x:          renderedX,
      y:          renderedY,
      containerW: wrapRect.width,
      containerH: wrapRect.height,
    };
  }, [positions, zoom, pan, svgH]);

  /* ── Node click ──────────────────────────────────────────────────────── */
  const handleNodeClick = useCallback((node) => {
    if (didDrag.current) return;
    const anchor = getAnchorPos(node.id);
    onSelectNode(node, anchor);
  }, [getAnchorPos, onSelectNode]);

  return (
    <div
      ref={wrapRef}
      className="relative w-full overflow-hidden rounded-[28px] border border-brand-dark bg-gradient-to-br from-brand-light via-white to-brand-light/60 shadow-inner"
      style={{ minHeight: 460, cursor: dragging.current ? 'grabbing' : 'grab' }}
    >
      <svg
        ref={svgRef}
        width="100%"
        height={Math.max(460, svgH * zoom + 80)}
        viewBox={`0 0 ${SVG_W} ${svgH}`}
        preserveAspectRatio="xMidYMid meet"
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        style={{ userSelect: 'none' }}
      >
        <g
          transform={`translate(${pan.x / zoom * zoom},${pan.y / zoom * zoom}) scale(${zoom})`}
          style={{ transformOrigin: '50% 50%' }}
        >
          <defs>
            <pattern id="ftgrid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#E5D9C3" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width={SVG_W} height={svgH} fill="url(#ftgrid)" opacity={0.5} />

          {links.map((link, i) => {
            const sp = positions[link.source];
            const tp = positions[link.target];
            if (!sp || !tp) return null;
            const isHighlighted = selectedNode
              ? link.source === selectedNode.id || link.target === selectedNode.id
              : false;
            const isDimmed = selectedNode ? !isHighlighted : false;
            return (
              <TreeEdge key={i}
                x1={sp.x} y1={sp.y} x2={tp.x} y2={tp.y}
                type={link.type}
                isHighlighted={isHighlighted}
                isDimmed={isDimmed}
              />
            );
          })}

          {nodes.map(node => {
            const pos = positions[node.id];
            if (!pos) return null;
            const isSelected    = selectedNode?.id === node.id;
            const isHighlighted = connectedIds ? connectedIds.has(node.id) : false;
            const isDimmed      = selectedNode ? (!isSelected && !isHighlighted) : false;
            return (
              <TreeNode key={node.id}
                node={node}
                x={pos.x} y={pos.y}
                isSelected={isSelected}
                isHighlighted={isHighlighted}
                isDimmed={isDimmed}
                onClick={handleNodeClick}
                verificationStatus={nodeVerifStatus?.[node.id]}
              />
            );
          })}
        </g>
      </svg>

      <div className="absolute bottom-4 left-4 flex flex-col gap-1 z-10">
        {[{ label: '+', delta: +0.2 }, { label: '−', delta: -0.2 }].map(btn => (
          <button key={btn.label}
            onClick={() => setZoom(z => Math.min(2, Math.max(0.4, z + btn.delta)))}
            className="w-8 h-8 bg-white border border-brand-dark rounded-lg text-accent-navy font-black text-base shadow hover:bg-brand-light transition-colors flex items-center justify-center">
            {btn.label}
          </button>
        ))}
        <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
          className="w-8 h-8 bg-white border border-brand-dark rounded-lg text-accent-navy font-black text-[9px] shadow hover:bg-brand-light transition-colors flex items-center justify-center"
          title="Reset view">⌂</button>
      </div>

      <div className="absolute bottom-4 right-4 bg-white/80 backdrop-blur-sm border border-brand-dark rounded-xl px-3 py-1.5 text-[9px] font-black text-text-muted uppercase tracking-widest">
        {Math.round(zoom * 100)}%
      </div>
    </div>
  );
};

export default FamilyTreeCanvas;